import {
  Component,
  ElementRef,
  HostListener,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Self,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { AccountingPeriod } from '@assetaccountant/api/models/accounting-period';
import { PeriodResolver } from '@assetaccountant/core/resolvers/period.resolver';
import { RegisterData, RegisterParams } from '@assetaccountant/core/resolvers/register.resolver';
import { getShortDateFormat, toUniversalDateString } from '@assetaccountant/core/utils/date-utils';
import {
  getCurrentRegisterLocale,
  TypedActivatedRoute,
} from '@assetaccountant/core/utils/route-utils';
import { NgbDate, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';

function isoToNgbDate(value: any): NgbDateStruct | null {
  const match = typeof value === 'string' && /^(\d{4})-(\d{2})-(\d{2})(?:$|\D)/.exec(value);
  if (match) {
    const [, year, month, day] = match;
    return {
      year: +year,
      month: +month,
      day: +day,
    };
  } else {
    return null;
  }
}

interface PeriodHighlightInfo {
  startDate: string;
  endDate: string;
  index: number;
  isCurrent: boolean;
}

@Component({
  selector: 'app-date-input',
  templateUrl: './date-input.component.html',
  styleUrls: ['./date-input.component.scss'],
  standalone: false,
})
export class DateInputComponent implements ControlValueAccessor, OnInit, OnDestroy {
  @Input() set minDate(value: string | null | undefined) {
    this.minDateStruct = isoToNgbDate(value);
  }
  @Input() set maxDate(value: string | null | undefined) {
    this.maxDateStruct = isoToNgbDate(value);
  }
  @Input() size: 'sm' | 'lg' | undefined;
  @Input() footerTemplate: TemplateRef<any> | undefined;

  @ViewChild('input') input?: ElementRef<HTMLInputElement>;

  modelStruct: NgbDateStruct | null = null;
  minDateStruct: NgbDateStruct | null = null;
  maxDateStruct: NgbDateStruct | null = null;

  value: string | null = null;

  get inputElement() {
    return this.input?.nativeElement;
  }

  disabled = false;

  placeholder: string = '';

  private periodHighlights: PeriodHighlightInfo[] = [];
  private periodsSub: Subscription | undefined;
  private todayIso: string = '';

  private _onChange?: (val: string | null) => void;
  private _onTouched?: () => void;

  constructor(
    @Inject(ActivatedRoute)
    private route: TypedActivatedRoute<RegisterParams, RegisterData>,
    @Self() @Optional() private control?: NgControl,
  ) {
    if (this.control) {
      this.control.valueAccessor = this;
    }
  }

  ngOnInit() {
    this.placeholder = getShortDateFormat(
      getCurrentRegisterLocale(this.route.snapshot),
    ).toLowerCase();

    const now = new Date();
    this.todayIso = toUniversalDateString(
      { year: now.getFullYear(), month: now.getMonth() + 1, day: now.getDate() },
      { throwOnFailure: true },
    );

    this.initPeriodHighlights();
  }

  ngOnDestroy() {
    this.periodsSub?.unsubscribe();
  }

  writeValue(value: any): void {
    this.modelStruct = isoToNgbDate(value);
    this.value = toUniversalDateString(this.modelStruct);
  }

  onChange(val: NgbDate | string | null) {
    /**
     * ngbDatePicker will return a date if there is 1 number,
     * we only want to return a value if there are 3 separate numbers.
     *
     * NOTE: we're deliberately not checking a particular format due to localisation,
     * e.g. AU vs US dates, China uses YYYY-MM-DD
     *
     * The AADateParserFormatter should handle 2 or 4 digit years
     * - are there 3 distinct numbers
     *
     * Regex explanation:
     * \d+ = one or more number characters
     * \D+ = one or more not number characters
     */
    if (
      val &&
      typeof val !== 'string' &&
      /\d+\D+\d+\D+\d+/.test(this.inputElement?.value || '') &&
      /^(\d{2}|\d{4})$/.test(val.year.toString())
    ) {
      this.value = toUniversalDateString(val);
    } else {
      this.value = null;
    }
  }

  @HostListener('keydown.enter')
  onEnter() {
    this.writeValue(this.value);
    this._onChange?.(this.value);
  }

  async onTouched() {
    await Promise.resolve();
    this.onEnter();
    this._onTouched?.();
  }

  registerOnChange(fn: (val: string | null) => void): void {
    this._onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
  }

  getPeriodForDate(date: NgbDateStruct): PeriodHighlightInfo | null {
    const iso = toUniversalDateString(date, { throwOnFailure: true });
    return this.periodHighlights.find(p => p.startDate <= iso && iso <= p.endDate) ?? null;
  }

  getPeriodParity(date: NgbDateStruct): 'even' | 'odd' | null {
    const period = this.getPeriodForDate(date);
    return period ? (period.index % 2 === 0 ? 'even' : 'odd') : null;
  }

  isToday(date: NgbDateStruct): boolean {
    return toUniversalDateString(date, { throwOnFailure: true }) === this.todayIso;
  }

  private initPeriodHighlights() {
    const periods$ = PeriodResolver.periods$;
    if (!periods$) {
      return;
    }

    this.periodsSub = periods$.subscribe(periods => {
      this.buildPeriodHighlights(periods);
    });
  }

  private buildPeriodHighlights(periods: AccountingPeriod[]) {
    this.periodHighlights = periods.map((p, index) => ({
      startDate: p.startDate,
      endDate: p.endDate,
      index,
      isCurrent: p.startDate <= this.todayIso && this.todayIso <= p.endDate,
    }));
  }
}
